import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { faBullhorn, faUsers } from "@fortawesome/pro-regular-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

import { OverviewHighlights as Highlights } from "../schema/overview";
import { formatNumber } from "../utils/percentage";

interface HighlightBoxProps {
	icon: IconDefinition;
	iconToneClass: string;
	labelKey: string;
	value: number;
	subLabelKey: string;
	subValue: number;
}

const HighlightBox: FunctionComponent<HighlightBoxProps> = ({
	icon,
	iconToneClass,
	labelKey,
	value,
	subLabelKey,
	subValue,
}) => {
	const { t } = useTranslation();

	return (
		<div className="flex flex-row items-center gap-4 rounded-xl border border-border-light bg-brand-white px-5 py-4">
			<div
				className={classNames(
					"flex items-center justify-center w-10 h-10 rounded-xl shrink-0",
					iconToneClass
				)}
			>
				<Icon className="w-4 h-4" icon={icon} />
			</div>
			<div className="flex-1 min-w-0">
				<div className="text-xs font-semibold uppercase tracking-wide text-text-meta">
					{t(labelKey)}
				</div>
				<div className="text-2xl font-bold text-text tabular-nums mt-0.5">
					{formatNumber(value)}
				</div>
			</div>
			<div className="flex flex-col items-end gap-0.5 pl-4 border-l border-border-light">
				<span className="text-xs font-semibold uppercase tracking-wide text-text-disabled text-right">
					{t(subLabelKey)}
				</span>
				<b className="text-base font-semibold text-text-meta tabular-nums">
					{formatNumber(subValue)}
				</b>
			</div>
		</div>
	);
};

export const OverviewHighlights: FunctionComponent<{ highlights: Highlights }> = ({
	highlights,
}) => (
	<div
		className="grid grid-cols-1 tb:grid-cols-2 gap-4 w-full mb-4"
		data-testid="overview-highlights"
	>
		<HighlightBox
			icon={faBullhorn}
			iconToneClass="bg-blue-action-200 text-brand-light"
			labelKey="analytics.overview.highlights.activeCampaigns"
			subLabelKey="analytics.overview.highlights.totalCampaigns"
			subValue={highlights.totalCampaigns}
			value={highlights.activeCampaigns}
		/>
		<HighlightBox
			icon={faUsers}
			iconToneClass="bg-status-success-light text-status-success"
			labelKey="analytics.overview.highlights.currentlyAssigned"
			// Windowed by the date filter, unlike the point-in-time figure beside it —
			// the label says so rather than letting the two look like the same number.
			subLabelKey="analytics.overview.highlights.assignedInPeriod"
			subValue={highlights.assignedLeadsInPeriod}
			value={highlights.currentlyAssignedLeads}
		/>
	</div>
);
